import { useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import Card, { CardHeader } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import PhoneInput from '../../components/ui/PhoneInput';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import KycFilesInput from '../../components/kyc/KycFilesInput';
import KycTagSelect from '../../components/kyc/KycTagSelect';
import { KYC_DOCUMENT_TYPES, KYC_OTHER_DOCUMENT_TAGS } from '../../lib/validators/kycSchema';
import { onboardInvestorSchema } from '../../lib/validators/onboardInvestorSchema';
import { useOnboardInvestorMutation } from '../../features/investors/onboardInvestorApi';
import { authErrorMessage } from '../../lib/authErrorMessage';
import { PlusIcon, TrashIcon } from '../../components/ui/icons';

const EMPTY_VALUES = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  send_credentials: true,
  document_type: '',
  documents: [],
  other_documents: [],
};

/**
 * POST /admin/investors — a separate, one-shot "create an already-active
 * client" flow, distinct from both Admin Management (which only manages
 * admin/super-admin accounts) and the KYC Review Queue (which reviews
 * self-submitted investor documents). Onboarding auto-approves KYC as part
 * of the same call — there's nothing to review afterward.
 */
export default function OnboardClient() {
  const toast = useToast();
  const [onboardInvestor, { isLoading }] = useOnboardInvestorMutation();
  const [result, setResult] = useState(null); // { email, temporaryPassword } | null
  const [copied, setCopied] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors },
  } = useForm({ resolver: zodResolver(onboardInvestorSchema), defaultValues: EMPTY_VALUES });

  const otherDocsArray = useFieldArray({ control, name: 'other_documents' });

  const onSubmit = async (values) => {
    try {
      const formData = new FormData();
      formData.append('first_name', values.first_name);
      formData.append('last_name', values.last_name);
      formData.append('email', values.email);
      formData.append('phone', values.phone);
      formData.append('send_credentials', values.send_credentials ? '1' : '0');
      formData.append('document_type', values.document_type);
      values.documents.forEach((file) => formData.append('documents[]', file));
      // v1.18.0 — optional "other" documents, index-keyed per Laravel's
      // array-of-objects multipart convention (documents[] alone can't
      // carry the paired tag). Unverified against a live response — see
      // kycTemplatesApi.js's header for the same network-unreachable
      // caveat; confirm this exact field naming before shipping.
      values.other_documents.forEach((row, idx) => {
        formData.append(`other_documents[${idx}][tag]`, row.tag);
        formData.append(`other_documents[${idx}][file]`, row.file);
      });

      const response = await onboardInvestor(formData).unwrap();
      // temporaryPassword is only shown once, right here, from the mutation
      // result — never re-fetched, never logged, never written anywhere
      // that persists (no localStorage, no redux cache field for it).
      setResult({ email: values.email, temporaryPassword: response.temporaryPassword });
      reset(EMPTY_VALUES);
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
      } else {
        setError('root', { message: authErrorMessage(err, { fallback: 'Could not onboard this client.' }) });
      }
    }
  };

  const copyPassword = async () => {
    if (!result?.temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      toast.push({ tone: 'success', message: 'Copied to clipboard.' });
    } catch {
      toast.push({ tone: 'warning', message: "Couldn't copy automatically — select and copy manually." });
    }
  };

  const closeResult = () => {
    setResult(null);
    setCopied(false);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Onboard client</h1>
        <p className="text-foreground-muted text-sm mt-1">
          Creates an already-active, pre-verified investor in one step. KYC is auto-approved — nothing lands in the review queue.
        </p>
      </div>

      <Card>
        <CardHeader title="Client details" />
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <Input label="First name" error={errors.first_name?.message} {...register('first_name')} />
            <Input label="Last name" error={errors.last_name?.message} {...register('last_name')} />
          </div>
          <Input label="Email address" type="email" error={errors.email?.message} {...register('email')} />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneInput
                label="Phone number"
                name="phone"
                value={field.value}
                onChange={field.onChange}
                error={errors.phone?.message}
              />
            )}
          />

          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">Document type</label>
            <Controller
              name="document_type"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {KYC_DOCUMENT_TYPES.map((t) => (
                    <label
                      key={t.value}
                      className={clsx(
                        'flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm cursor-pointer transition-colors',
                        field.value === t.value
                          ? 'border-accent bg-accent-soft text-accent'
                          : 'border-border text-foreground-muted hover:border-border-strong'
                      )}
                    >
                      <input
                        type="radio"
                        className="accent-accent"
                        checked={field.value === t.value}
                        onChange={() => field.onChange(t.value)}
                      />
                      {t.label}
                    </label>
                  ))}
                </div>
              )}
            />
            {errors.document_type && <p className="mt-1.5 text-xs text-danger">{errors.document_type.message}</p>}
          </div>

          <Controller
            name="documents"
            control={control}
            render={({ field }) => (
              <KycFilesInput value={field.value} onChange={field.onChange} error={errors.documents?.message} />
            )}
          />

          {/* v1.18.0 — optional, one row per "other" document tag. Distinct
              from the identity document above: these never gate anything,
              just pre-fill the investor's Other Documents checklist so they
              don't have to upload it themselves later. */}
          <div className="rounded-xl border border-border p-3.5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Other documents (optional)</p>
                <p className="text-xs text-foreground-subtle mt-0.5">
                  Pre-attach forms like a Fixed Income Agreement — doesn't affect KYC approval.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => otherDocsArray.append({ tag: '', file: undefined })}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add
              </Button>
            </div>

            {otherDocsArray.fields.map((field, idx) => (
              <div key={field.id} className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto] gap-2 items-start rounded-lg bg-background-elevated p-3">
                <Controller
                  name={`other_documents.${idx}.tag`}
                  control={control}
                  render={({ field: tagField }) => (
                    <KycTagSelect
                      label={null}
                      value={tagField.value}
                      onChange={tagField.onChange}
                      knownTags={KYC_OTHER_DOCUMENT_TAGS}
                      error={errors.other_documents?.[idx]?.tag?.message}
                    />
                  )}
                />
                <div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    onChange={(e) =>
                      setValue(`other_documents.${idx}.file`, e.target.files?.[0], { shouldValidate: true })
                    }
                    className="w-full text-xs text-foreground-muted file:mr-2 file:rounded-lg file:border-0 file:bg-background-surface file:px-2.5 file:py-2 file:text-xs file:font-medium file:text-foreground file:cursor-pointer"
                  />
                  {errors.other_documents?.[idx]?.file && (
                    <p className="mt-1 text-xs text-danger">{errors.other_documents[idx].file.message}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => otherDocsArray.remove(idx)}
                  className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg text-foreground-subtle hover:text-danger hover:bg-danger-soft transition-colors"
                  aria-label="Remove"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <Controller
            name="send_credentials"
            control={control}
            render={({ field }) => (
              <label className="flex items-start gap-3 rounded-xl border border-border bg-background-elevated p-3.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-accent"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                <span className="text-sm">
                  <span className="block text-foreground font-medium">Email login credentials to the client</span>
                  <span className="block text-foreground-muted text-xs mt-0.5">
                    {field.value
                      ? 'On (default): a temporary password is emailed directly to them. You will not see it.'
                      : 'Off: nothing is emailed — a one-time password is shown here after creation for you to share yourself.'}
                  </span>
                </span>
              </label>
            )}
          />

          {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

          <Button type="submit" className="w-full" isLoading={isLoading}>
            Onboard client
          </Button>
        </form>
      </Card>

      <Modal open={!!result} onClose={closeResult} title="Client onboarded">
        {result && (
          <div className="space-y-4">
            {result.temporaryPassword ? (
              <>
                <p className="text-sm text-danger font-medium">Copy this password now — it won't be shown again.</p>
                <div className="flex items-center gap-2 rounded-xl border border-border bg-background-elevated px-3.5 py-2.5">
                  <code className="flex-1 text-sm text-foreground break-all">{result.temporaryPassword}</code>
                  <Button type="button" size="sm" variant="secondary" onClick={copyPassword}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-foreground-subtle">
                  Share this with {result.email} yourself — it isn't stored anywhere and can't be retrieved again once you close this.
                </p>
              </>
            ) : (
              <p className="text-sm text-foreground-muted">
                Login credentials were emailed directly to {result.email}. No password is shown here.
              </p>
            )}
            <Button type="button" className="w-full" onClick={closeResult}>
              Done
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
